import type {
  CountApiRoi,
  RoiPlacementActivity,
  RoiRedeploymentGrid,
  RoiSummary,
} from 'src/types/roi';
import type {TimeFrame} from 'src/utils/date-range';

import * as React from 'react';
import {Link, useHistory, useLocation} from 'src/rerouter';
import {useDispatch, useSelector} from 'react-redux';
import uniqueId from 'lodash/uniqueId';
import floor from 'lodash/floor';

import classify from 'src/utils/classify';
import {formatFractionNumber, formatPercentage} from 'src/utils/number';
import {checkRoiV3ApiEnabled} from 'src/utils/analytics-api-migration';

import {startDataExport} from 'src/action-creators/data-export-report';

import {selectNpsData} from 'src/selectors/agency-overview';
import {selectCorporateLabels} from 'src/selectors/language-support';
import useDisplayConfig from 'src/hooks/useDisplayConfig';

import {getPlacementDurationMean} from 'src/components/analytics/performance/charts/placement-durations';
import {getAverageRedeploymentDays} from 'src/components/analytics/figures/redeployment-grid';
import {useContextTip} from 'src/components/lib/mouse-tip/mouse-tip';

import NPSZeroState from 'src/images/nps-zero-state-foreground.svg';
import Info from 'src/images/icons/info.svg';
import LinkIcon from 'src/images/icons/link-icon.svg';
import {useAudienceFilterSeparation} from 'src/hooks/product-flags';

import css from './roi.css';

import useLabelTranslation from 'src/hooks/useLabelTranslation';


function shrinkHours(hours: number): {
  dayValue: number;
  dayUnit: string;
} {
  const dayValue = hours < 2_080 ? hours / 40 : hours / 2_080;
  // 2_080 -> working hours in a year (40 x 52), 40 -> hours in a week
  return {
    dayValue: floor(dayValue, 1),
    dayUnit: hours < 2_080 ? 'weeks' : 'years',
  };
}

const ZeroStateBar = () => (
  <div className={css.zeroStateBarOuter}>
    <div className={css.zeroStateBarInner}></div>
    <div className={css.zeroStateBarInner2}></div>
  </div>
);

const LabeledFigures = ({
  figures,
}: {
  figures: {
    label: string | React.ReactNode;
    value: number | null | undefined;
  }[];
}) => (
  <div>
    {figures.map(({label, value}) => (
      <div className={css.figureContainer}>
        <div className={css.figureLabel}>{label}</div>
        <div className={css.figureValue}>
          {formatFractionNumber(Number(value))}
        </div>
      </div>
    ))}
  </div>
);

const labelTipId = uniqueId('tooltip_');

const ContentWithGlossary = ({children}: {children: React.ReactNode}) => {
  const {t} = useLabelTranslation();
  return (
    <p className={css.tipContent}>
      <span>{children}</span>
      <span>{` ${t('See_the', 'See the')} `}</span>
      <span>
        <a
          href="https://support.sensehq.co/hc/en-us/sections/360011146471-ROI"
          className={css.glossaryLink}
          target="_blank"
        >
          {t('glossary', 'glossary')}
        </a>
      </span>
    </p>
  );
};

const ToolTip = ({content}: {content: string | React.ReactNode}) => {
  const {language} = useLabelTranslation();
  const contentWithGlossary = React.useMemo(
    () => <ContentWithGlossary>{content}</ContentWithGlossary>,
    [language],
  );

  const [componentRef, handleContextMenu, handleClose, isActive] =
    useContextTip({
      id: labelTipId,
      content: contentWithGlossary,
      fixedTo: 'bottom',
    });

  return (
    <div
      // @ts-expect-error - TS2322 - Type 'ReactRef<HTMLElement>' is not assignable to type 'Ref<HTMLDivElement> | undefined'.
      ref={componentRef}
      onContextMenu={handleContextMenu}
      onClick={isActive ? handleClose : handleContextMenu}
    >
      <Info className={css.infoIcon} />
    </div>
  );
};

export const PlacementRoi = ({
  roi,
  timeFrame,
}: {
  roi: CountApiRoi | null | undefined;
  timeFrame: TimeFrame;
}) => {
  const {candidates_reached, influenced_placements, fte_hours, time_saved} =
    roi || {};
  const {t, makeKey} = useLabelTranslation();
  const corporateLabels = useSelector(selectCorporateLabels);
  const zeroState = !roi || (!candidates_reached && !influenced_placements);
  // @ts-expect-error - TS18048 - 'candidates_reached' is possibly 'undefined'. | TS18048 - 'influenced_placements' is possibly 'undefined'.
  const hasROIData = candidates_reached > 0 && influenced_placements > 0;
  // @ts-expect-error - TS18048 - 'candidates_reached' is possibly 'undefined'.
  const hidePlacementData = candidates_reached > 0 && !influenced_placements;

  const dispatch = useDispatch();

  const location = useLocation();
  const isInAnalytics = location.pathname.includes('analytics');

  const fteTimeSaved = checkRoiV3ApiEnabled() ? time_saved : fte_hours;

  const {dayValue, dayUnit} = (roi && shrinkHours(Number(fteTimeSaved))) || {};

  const figures = roi
    ? [
        {
          label:
            t(corporateLabels['sense_candidates'], 'Candidates') + ' reached',
          value: Number(candidates_reached) || 0,
        },
        {
          label: (
            <div>
              {`${t('FTE_hours', 'FTE hours')} `}
              <br />
              {t('saved', 'saved')}
            </div>
          ),
          value: Number(fteTimeSaved) || 0,
        },
        {
          label: (
            <div>
              {`${t('FTE', 'FTE')} ${
                dayUnit
                  ? t(makeKey(dayUnit.toUpperCase()), dayUnit.toUpperCase())
                  : ''
              }`}{' '}
              <br />
              {t('saved', 'saved')}
            </div>
          ),
          value: Number(dayValue) || 0,
        },
      ]
    : [];

  const handleExportPlacements = () => {
    dispatch(
      startDataExport(
        `data-exports/${
          checkRoiV3ApiEnabled() ? 'roi-highlights' : 'roi-report'
        }`,
        {
          date_range: timeFrame,
        },
      ),
    );
  };

  return (
    <div className={classify(css.section, css.roiSection)}>
      <div className={css.headerRow}>
        <h3 className={css.sectionHeader}>
          {hasROIData &&
            `${t('Sense_influenced', 'Sense influenced')} ` +
              t(
                corporateLabels['sense_placements'],
                'placements',
              ).toLowerCase()}
          {zeroState &&
            `${t('Sense_influenced', 'Sense influenced')} ` +
              t(corporateLabels['sense_placement'], 'placement').toLowerCase() +
              ` ${t('funnel', 'funnel')}`}
          {hidePlacementData &&
            t(
              'Sense_influenced_return_on_investment',
              'Sense influenced return on investment',
            )}
        </h3>
        {hasROIData && (
          <ToolTip
            content={
              <div>
                <b>
                  {t(corporateLabels['sense_candidates'], 'Candidates')}{' '}
                  {t('reached', 'reached')}{' '}
                </b>
                {t('are_unique', 'are unique')}{' '}
                {t(
                  corporateLabels['sense_candidates'],
                  'candidates',
                ).toLowerCase()}{' '}
                {`${t(
                  'that_you_messaged_via_Sense_touchpoints',
                  'that you messaged via Sense touchpoints',
                )}.`}{' '}
                <b>{t(corporateLabels['sense_placements'], 'Placements')} </b>
                {t(
                  'are_counted_as_influenced_by_Sense_if_they_occurred_within_90_days_of_messaging_those',
                  'are counted as influenced by Sense if they occurred within 90 days of messaging those',
                )}{' '}
                {t(
                  corporateLabels['sense_candidates'],
                  'candidates',
                ).toLowerCase()}
                .
              </div>
            }
          />
        )}
        {hidePlacementData && (
          <ToolTip
            content={
              `${t(corporateLabels['sense_candidates'], 'Candidates')} ` +
              `${t('reached', 'reached')} ` +
              `${t(
                corporateLabels['sense_candidates'],
                'candidates',
              ).toLowerCase()} ` +
              `${t(
                'that_you_messaged_via_Sense_touchpoints',
                'that you messaged via Sense touchpoints.',
              )} ` +
              `${t(
                'Each_of_these_automated_customized_messages_saves_a_human_recruiters_a_few_minutes_of_time_and_those_minutes_add_up_weeks_and_then_months',
                'Each of these automated customized messages saves a human recruiters a few minutes of time, and those minutes add up weeks and then months',
              )}.` +
              `${t(
                'That_saves_expenses_andor_frees_recruiters_to_do_more_important_tasks',
                'That saves expenses and/or frees recruiters to do more important tasks',
              )}.`
            }
          />
        )}
      </div>
      {hasROIData && (
        <>
          <div className={css.placementsRoiContainer}>
            <div
              className={classify(
                css.columnSection,
                css.candidateFigureContainer,
              )}
            >
              <div
                className={classify(css.figureLabel, css.candidateFigureColor)}
              >
                {t(
                  corporateLabels['sense_candidates'],
                  'candidates',
                ).toLowerCase()}
                <br />
                {t('reached', 'reached')}
              </div>
              <div
                className={classify(css.figureValue, css.candidateFigureColor)}
              >
                {formatFractionNumber(Number(candidates_reached))}
              </div>
            </div>

            <div
              className={classify(
                css.columnSection,
                css.placementsFigureContainer,
              )}
            >
              <div className={css.placementsFigureTop}>
                <div className={css.placementsFigureTopInner} />
              </div>
              <div
                className={classify(
                  css.columnSection,
                  css.placementsFigureInnerContainer,
                )}
              >
                <div
                  className={classify(
                    css.figureLabel,
                    css.placementFigureColor,
                  )}
                >
                  {t(
                    corporateLabels['sense_placements'],
                    'placements',
                  ).toLowerCase()}
                </div>
                <div
                  className={classify(
                    css.figureValue,
                    css.placementFigureColor,
                  )}
                >
                  {formatFractionNumber(Number(influenced_placements))}
                </div>
              </div>
            </div>
          </div>
          {isInAnalytics ? (
            <div onClick={handleExportPlacements} className={css.link}>
              <span>{t('Export_a_report', 'Export a report')}</span>
            </div>
          ) : (
            <Link to={'/analytics/performance'} className={css.link}>
              <span>
                {t('View_export_full_report', 'View & export full report')}
              </span>
              <LinkIcon className={css.linkIcon} />
            </Link>
          )}
        </>
      )}
      {}
      {hidePlacementData && <LabeledFigures figures={figures} />}
      {zeroState && (
        <>
          <ZeroStateBar />
          <div className={css.zeroStateBarCaption}>
            <div>{t('ENGAGED', 'ENGAGED')}</div>
            <div>{t('PLACED', 'PLACED')}</div>
          </div>
          <p className={css.description}>
            {`${t('Generate', 'Generate')} ` +
              `${t(
                corporateLabels['sense_placement'],
                'placement',
              ).toLowerCase()} ` +
              `${t(
                'revenue_by_sending_targeted_personalized_messages_at_key_moments_throughout_the',
                'revenue by sending targeted personalized messages at key moments throughout the',
              )} ` +
              `${t(
                corporateLabels['sense_candidate'],
                'candidate',
              ).toLowerCase()} ` +
              `${t('lifecycle', 'lifecycle')} ` +
              `${t(
                'Sense\'s_impact_on_your_bottom_line_will_appear_in_this_chart',
                'Sense\'s impact on your bottom line will appear in this chart',
              )}`}
          </p>
          <a
            href="https://support.sensehq.co/hc/en-us/articles/360051621351-Candidate-to-Placement-Conversions"
            className={css.link}
            target="_blank"
          >
            <span>
              {t('Engage', 'Engage')}{' '}
              {t(
                corporateLabels['sense_candidates'],
                'candidates',
              ).toLowerCase()}{' '}
              {t('to_generate', 'to generate')}{' '}
              {t(
                corporateLabels['sense_placements'],
                'placements',
              ).toLowerCase()}{' '}
            </span>
            <LinkIcon className={css.linkIcon} />
          </a>
        </>
      )}
    </div>
  );
};

export const SavingsRoi = ({roi}: {roi: CountApiRoi | null | undefined}) => {
  const {time_saved, fte_hours} = roi || {};
  const fteTimeSaved = Number(checkRoiV3ApiEnabled() ? time_saved : fte_hours);
  const {t} = useLabelTranslation();
  const corporateLabels = useSelector(selectCorporateLabels);
  const {dayValue, dayUnit} = (roi && shrinkHours(Number(fteTimeSaved))) || {};

  const figures = roi
    ? [
        {
          label: (
            <div>
              {`${t('FTE_hours', 'FTE hours')} `}
              <br />
              {t('saved', 'saved')}
            </div>
          ),
          value: fteTimeSaved,
        },
        {
          label: (
            <div>
              {/* @ts-expect-error - TS18048 - 'dayUnit' is possibly 'undefined'. */}
              {`${t('FTE', 'FTE')} ${dayUnit.toUpperCase()}`} <br />
              {t('saved', 'saved')}
            </div>
          ),
          value: dayValue,
        },
      ]
    : [];

  return (
    <div className={classify(css.section, css.roiSection)}>
      <div className={css.headerRow}>
        <h3 className={css.sectionHeader}>{t('Savings', 'Savings')}</h3>
        {fteTimeSaved > 0 && (
          <ToolTip
            content={`${t(
              'Sending_a_single_automated_customized_message_via_Sense_saves_a_human_recruiter_a_few_minutes_of_time_Sending_thousands_adds_up_to_weeks_then_months_freeing_recruiters_to_do_more_important_tasks',
              'Sending a single automated customized message via Sense saves a human recruiter a few minutes of time. Sending thousands adds up to weeks, then months, freeing recruiters to do more important tasks',
            )}.`}
          />
        )}
      </div>
      {roi && fteTimeSaved > 0 && <LabeledFigures figures={figures} />}

      {!fteTimeSaved && (
        <>
          <ZeroStateBar />
          <div className={css.zeroStateBarCaption}>
            <div>{t('MANUAL', 'MANUAL')}</div>
            <div>{t('AUTOMATIC', 'AUTOMATIC')}</div>
          </div>
          <p className={css.description}>
            {`${t(
              'Save_thousands_of_hours_of_human_resources_by_personalizing_messages_and_transcribing',
              'Save thousands of hours of human resources by personalizing messages and transcribing',
            )} ` +
              `${t(
                corporateLabels['sense_candidate'],
                'candidate',
              ).toLowerCase()} ` +
              `${t(
                'responses_to_your_ATS_automatically',
                'responses to your ATS automatically',
              )}.` +
              `${t(
                'The_hours_youve_saved_will_appear_in_this_chart',
                'The hours you’ve saved will appear in this chart',
              )}`}
          </p>
          <a
            href="https://support.sensehq.co/hc/en-us/articles/360052215571-Time-Saved"
            className={css.link}
            target="_blank"
          >
            <span>
              {t('Time_saved_via_automations', 'Time saved via automations')}
            </span>
            <LinkIcon className={css.linkIcon} />
          </a>
        </>
      )}
    </div>
  );
};

export const CandidateNPS = () => {
  const npsData = useSelector(selectNpsData);
  const {score, promoters, passive, detractors} = npsData || {};
  const {t} = useLabelTranslation();
  const corporateLabels = useSelector(selectCorporateLabels);
  // @ts-expect-error - TS18048 - 'promoters' is possibly 'undefined'. | TS18048 - 'passive' is possibly 'undefined'. | TS18048 - 'detractors' is possibly 'undefined'.
  const total = promoters + passive + detractors;
  const isJourneyV2 = useAudienceFilterSeparation();
  const journeyPageRoute = isJourneyV2
    ? '/journeys/goal-selection'
    : '/journey/new';

  return (
    <div
      className={classify(css.section, {
        [css.npsCompact]: !score,
        [css.npsExpanded]: score,
      })}
    >
      <div className={css.headerRow}>
        <h3 className={css.sectionHeader}>
          {t(corporateLabels['sense_candidate'], 'Candidate')} {t('NPS', 'NPS')}
        </h3>
        {score ? (
          <ToolTip
            content={
              <div>
                {`${t(
                  'This_number_is_the_average_of',
                  'This number is the average of',
                )} `}
                <b>{`${t('Net_Promoter_Score', 'Net Promoter Score')} `}</b>
                {`${t(
                  'responses_to_your_surveys_and_chatbots_that_contained_an_NPS_question',
                  'responses to your surveys and chatbots that contained an NPS question',
                )}.`}
              </div>
            }
          />
        ) : (
          ''
        )}
      </div>
      <div className={css.npsContents}>
        {score ? <Dial rawValue={Math.round(score)} /> : <ZeroStateDial />}
        {score ? (
          <div className={css.npsBreakdown}>
            <div className={css.npsPromoters}>{`${t(
              'PROMOTERS',
              'PROMOTERS',
              // @ts-expect-error - TS18048 - 'promoters' is possibly 'undefined'.
            )} ${formatPercentage(promoters / total)}`}</div>
            <div className={css.npsBar}>
              <div
                className={css.npsPromoterBar}
                // @ts-expect-error - TS18048 - 'promoters' is possibly 'undefined'.
                style={{width: `${formatPercentage(promoters / total)}`}}
              />
              <div
                className={css.npsPassiveBar}
                // @ts-expect-error - TS18048 - 'passive' is possibly 'undefined'.
                style={{width: `${formatPercentage(passive / total)}`}}
              />
              {/* @ts-expect-error - TS18048 - 'detractors' is possibly 'undefined'. */}
              <div style={{width: `${formatPercentage(detractors / total)}`}} />
            </div>
            <div className={css.npsBottom}>
              <div className={css.npsBottomx}>{`${t(
                'PASSIVES',
                'PASSIVES',
                // @ts-expect-error - TS18048 - 'passive' is possibly 'undefined'.
              )} ${formatPercentage(passive / total)}`}</div>
              <div className={css.detractorLabel}>{`${t(
                'DETRACTORS',
                'DETRACTORS',
                // @ts-expect-error - TS18048 - 'detractors' is possibly 'undefined'.
              )} ${formatPercentage(detractors / total)}`}</div>
            </div>
          </div>
        ) : (
          <div className={css.columnSection}>
            <div className={css.zeroStateNpsCaption}>
              {t('PROMOTER', 'PROMOTER')}
            </div>
            <div className={css.zeroStateNpsBarSolid}></div>
            <div className={css.zeroStateNpsBar}></div>
            <div className={css.zeroStateNpsBarSolid}></div>
            <div className={css.zeroStateNpsCaption}>
              {t('DETRACTOR', 'DETRACTOR')}
            </div>
          </div>
        )}
      </div>
      {score ? (
        <Link to={'/reports/surveys-and-nps'} className={css.link}>
          <span>{t('View_full_report', 'View full report')}</span>
          <LinkIcon className={css.linkIcon} />
        </Link>
      ) : (
        <Link to={journeyPageRoute} className={css.link}>
          <span>
            {t('Create_a_net_promoter_survey', 'Create a net promoter survey')}
          </span>
          <LinkIcon className={css.linkIcon} />
        </Link>
      )}
    </div>
  );
};

export const Highlights = ({
  placementActivity,
  placementDurations,
  redeploymentGrid,
  newHires,
  redeploys,
}: {
  placementActivity?: RoiPlacementActivity;
  placementDurations?: RoiSummary;
  redeploymentGrid?: RoiRedeploymentGrid;
  newHires?: number;
  redeploys?: number;
}) => {
  const placementDurationMean = placementDurations
    ? getPlacementDurationMean(placementDurations)
    : 0;
  const averageRedeploymentDays = redeploymentGrid
    ? getAverageRedeploymentDays(redeploymentGrid)
    : 0;
  const numFirstPlacements = !checkRoiV3ApiEnabled()
    ? placementActivity?.numFirstPlacements
    : newHires || 0;
  const numRedeployments = !checkRoiV3ApiEnabled()
    ? placementActivity?.numRedeployments
    : redeploys || 0;
  const total = Number(numFirstPlacements) + Number(numRedeployments);
  const {t} = useLabelTranslation();
  const corporateLabels = useSelector(selectCorporateLabels);
  const displayConfig = {
    analytics_performance_redeploys: useDisplayConfig(
      'analytics_performance_redeploys',
    ),
    analytics_performance_days_to_redeploy: useDisplayConfig(
      'analytics_performance_days_to_redeploy',
    ),
    analytics_performance_months_assigned: useDisplayConfig(
      'analytics_performance_months_assigned',
    ),
  } as const;

  return (
    <div className={classify(css.section, css.npsCompact)}>
      <h3 className={css.sectionHeader}>{t('Highlights', 'Highlights')}</h3>
      {total > 0 && (
        <div className={css.highlightsContent}>
          <div className={css.highlightRow}>
            <div className={css.highlightLabel}>
              {t('New', 'New')}{' '}
              {t(
                corporateLabels['sense_placements'],
                'placements',
              ).toLowerCase()}
            </div>
            <div className={css.highlightValue}>{numFirstPlacements}</div>
          </div>
          {displayConfig.analytics_performance_redeploys && (
            <div className={css.highlightRow}>
              <div className={css.highlightLabel}>
                {t('Redeploys', 'Redeploys')}
              </div>
              <div className={css.highlightValue}>{numRedeployments}</div>
            </div>
          )}
          {displayConfig.analytics_performance_days_to_redeploy && (
            <div className={css.highlightRow}>
              <div className={css.highlightLabel}>
                {t('Days_to_Redeploy', 'Days to Redeploy')}
              </div>
              <div className={css.highlightValue}>
                {averageRedeploymentDays}
              </div>
            </div>
          )}
          {displayConfig.analytics_performance_months_assigned && (
            <div className={css.highlightRow}>
              <div className={css.highlightLabel}>
                {t('Months_assigned', 'Months assigned')}
              </div>
              <div className={css.highlightValue}>{placementDurationMean}</div>
            </div>
          )}
        </div>
      )}
      {(isNaN(total) || total === 0) && (
        <div className={css.description}>
          {`${t(
            'This_performance_metrics_page_gives_you_insight_into_placements_including_the_redeployment_rate_average_assignment_length_and_more',
            'This performance metrics page gives you insight into placements, including the redeployment rate, average assignment length, and more',
          )}. 
          ${t(
            'Key_metrics_about_your_first_placements_will_appear_here',
            'Key metrics about your first placements will appear here.',
          )}`}
        </div>
      )}
      {(isNaN(total) || total === 0) && (
        <Link to={'/analytics'} className={css.link}>
          <span>
            {t('About_performance_metrics', 'About performance metrics')}
          </span>
          <LinkIcon className={css.linkIcon} />
        </Link>
      )}
    </div>
  );
};

const ZeroStateDial = () => <NPSZeroState className={css.zeroStateNpsDial} />;

export function Dial({rawValue = 50}: {rawValue?: number}) {
  const value = rawValue > 0 ? rawValue / 100 : 0;

  const originAngle = Math.PI / 2;
  const addAngle = 0.7 * Math.PI;
  const startAngle = originAngle + addAngle;

  const totalAngle = addAngle * 2;
  const totalAngleDegrees = (totalAngle * 180) / Math.PI;

  const endX = Math.cos(startAngle - totalAngle);
  const endY = Math.sin(startAngle - totalAngle);

  const outerR = 50;
  const outerEndX = endX * outerR + 50;
  const outerEndY = endY * -outerR + 50;
  const outerStartX = -endX * outerR + 50;
  const outerStartY = outerEndY;

  const innerR = 37;
  const innerEndX = endX * innerR + 50;
  const innerEndY = endY * -innerR + 50;
  const innerStartX = -endX * innerR + 50;
  const innerStartY = innerEndY;

  const progressAngle = totalAngle * value;
  const endAngle = startAngle - progressAngle;
  const progressEnd = {
    x: Math.cos(endAngle),
    y: Math.sin(endAngle),
  } as const;
  const progressOuterEnd = {
    x: progressEnd.x * outerR + 50,
    y: progressEnd.y * -outerR + 50,
  } as const;
  const progressInnerEnd = {
    x: progressEnd.x * innerR + 50,
    y: progressEnd.y * -innerR + 50,
  } as const;

  const middleR = (outerR - innerR) / 2 + innerR;
  const triangleEndAngle = endAngle - 0.05 * Math.PI;
  const pointerEnd = {
    x: Math.cos(triangleEndAngle) * middleR + 50,
    y: Math.sin(triangleEndAngle) * -middleR + 50,
  } as const;

  const progressIsLarge = progressAngle > Math.PI ? 1 : 0;

  let textLength = 20;
  if (String(rawValue).length === 3) {
    textLength = 30;
  } else if (String(rawValue).length === 4) {
    textLength = 35;
  }

  return (
    <svg width="100" height="100" viewBox="0 0 100 100">
      <circle cx="50" cy="50" r="30" fill="#F7F7F7" />
      <text
        x="50%"
        y="48%"
        text-anchor="middle"
        stroke={value > 0 ? '#196E46' : '#E71D42'}
        stroke-width="2px"
        dy=".4em"
        textLength={textLength}
        lengthAdjust="spacing"
      >
        {rawValue}
      </text>
      <path
        d={`M ${outerStartX},${outerStartY}
          A ${outerR},${outerR} ${totalAngleDegrees} 1 1 ${outerEndX},${outerEndY}
          L ${innerEndX},${innerEndY}
          A ${innerR},${innerR} ${totalAngleDegrees} 1 0 ${innerStartX},${innerStartY}
          Z`}
        fill="#dddddd"
      />
      {value > 0 && (
        <path
          d={`M ${outerStartX},${outerStartY}
          A ${outerR},${outerR} ${totalAngleDegrees} ${progressIsLarge} 1 ${progressOuterEnd.x},${progressOuterEnd.y}
          L ${pointerEnd.x},${pointerEnd.y}
          L ${progressInnerEnd.x},${progressInnerEnd.y}
          A ${innerR},${innerR} ${totalAngleDegrees} ${progressIsLarge} 0 ${innerStartX},${innerStartY}
          Z`}
          fill="#196e46"
        />
      )}
    </svg>
  );
}
